import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { download } from "utils/download";
import { getParameterCaseInsensitive } from "utils/getParameterCaseInsensitive";
import { actionProgressStore } from "Components/ActionProgress/ActionProgressStore";
import i18n, { I18N_NS } from "Config/i18n";
// import { isEqual } from "lodash";

class ViewsStore extends CoreStore {
  data: API_ViewInformation[] = [];
  total: number;
  files: API_ViewInformation[] = [];
  currentViewId: number = null;
  fromSavedFilter: boolean = false;
  stillAvailableMetadata: { [key: string]: string[] };
  lastParams: API_ViewRequest;
  navigateToFileId: number;
  viewInformation: API_ViewInformation = {};
  abortController: AbortController = null;
  countFilesAbortController: AbortController = null;
  filterableFileTypes: string[] = [];
  filterableStatus: number[] = [];
  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      total: observable,
      files: observable,
      currentViewId: observable,
      fromSavedFilter: observable,
      filterableFileTypes: observable,
      filterableStatus: observable,
      createExcel: action.bound,
      getById: action.bound,
      loadMoreData: action.bound,
      getFilesFromIds: action.bound,
      downloadFiles: action.bound,
      setViewId: action.bound,
      currentView: computed,
      fetch: action.bound,
      fetchExportOptions: action.bound,
      searchFilesByFilter: action.bound,
      fetchFilters: action.bound,
    });
  }

  clear() {
    this.data = [];
    this.clearFiles();
    this.currentViewId = null;
    this.fromSavedFilter = false;
  }

  clearFiles() {
    this.lastParams = null;
    this.files = [];
    this.stillAvailableMetadata = null;
    this.total = 0;
    this.filterableFileTypes = [];
    this.filterableStatus = [];
  }

  url(id?: number) {
    return id
      ? API_URL.VIEWS.searchFilesByFilterMetadata(projectsDataStore.projectId, this.currentViewId)
      : API_URL.VIEWS.getAllViews_1(projectsDataStore.projectId);
  }

  @handleStoreState()
  async fetch() {
    try {
      const response: API_ViewResponse = await API.getData(this.url());
      runInAction(() => {
        this.data = response.views;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetchExportOptions() {
    try {
      const response: API_ViewExcelExportOptions = await API.getData(
        API_URL.VIEWS.getExportOptions(projectsDataStore.projectId, this.currentViewId)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createExcel(formData: API_ViewExcelMultipleFilesRequest) {
    try {
      const file = await API.newData(
        API_URL.VIEWS.getCustomizedExcelList(projectsDataStore.projectId, this.currentViewId),
        formData,
        undefined,
        { responseType: "blob" }
      );
      download(file, file.name, "application/vnd.ms-excel");
    } catch (error) {
      throw error;
    }
  }

  getById(viewId: number) {
    return this.data.find((item) => item.id === viewId);
  }

  @handleStoreState()
  /**
   * @fromSavedFilter - boolean. To point out that request body is picked from saved filters.
   *  Used to trigger different icon in SaveFiltersButton.tsx
   */
  async searchFilesByFilter(formBody: API_ViewRequest, fromSavedFilter?: boolean) {
    formBody.limit = 100;
    formBody.offset = 0;
    this.files = [];
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    if (this.navigateToFileId) {
      formBody.limit = 5000;
    }
    // let shouldRefetchAmountOfFiles = true;
    // if (this.lastParams) {
    //   const { sortBy: _, sortOrder: _1, ...lastParams } = this.lastParams || {};
    //   const { sortBy: _2, sortOrder: _3, ...thisParams } = formBody;
    //   if (isEqual(lastParams, thisParams)) {
    //     shouldRefetchAmountOfFiles = false;
    //   }
    // }
    this.lastParams = { ...formBody };
    try {
      const response = await API.newData<API_ViewInformationResponse>(this.url(this.currentViewId), formBody, null, {
        signal: this.abortController.signal,
      });
      runInAction(() => {
        if (response.data) {
          this.files = response.data.slice();
          this.total = response.data.length;
          this.stillAvailableMetadata = response.metadata;
          this.fromSavedFilter = !!fromSavedFilter;
          if (this.navigateToFileId) {
            this.navigateToFileId = undefined;
          }
        }
        if (response.filterableFileTypes?.length) {
          this.filterableFileTypes = response.filterableFileTypes;
        }
        if (response.filterableStatus?.length) {
          this.filterableStatus = response.filterableStatus;
        }
      });

      if (response.data?.length === formBody.limit) {
        runInAction(() => {
          this.total = response.data.length + 1;
        });
        this.fetchAmount(formBody);
      } else {
        runInAction(() => {
          if (this.countFilesAbortController?.abort) {
            this.countFilesAbortController.abort();
          }
          this.total ||= response.data.length;
        });
      }
    } catch (error) {
      throw error;
    } finally {
      this.abortController = null;
    }
  }

  async fetchAmount(formBody: API_ViewRequest) {
    try {
      if (this.countFilesAbortController) {
        this.countFilesAbortController.abort();
      }
      // The "LOADING" list item is checked in the ScrollableTable to distinguish
      // between the really missing file and displaying of lazy-loading while fetching
      // total amount of items behind the scenes.
      // This line of code will add a placeholder row to show that the loading is in progress
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.files.push("LOADING" as any);
      this.countFilesAbortController = new AbortController();
      const amount = await API.newData<number>(
        API_URL.VIEWS.getNumberOfExpectedFiles(projectsDataStore.projectId, this.currentViewId),
        formBody,
        null,
        { signal: this.countFilesAbortController.signal }
      );
      runInAction(() => {
        this.files.pop();
      });
      // timeout is needed for UI to react on deleting of the placeholder row and set the actual amount of files
      setTimeout(() => {
        runInAction(() => {
          this.files.length = amount;
          this.total = amount;
        });
      }, 1000);
    } catch (error) {
      throw error;
    } finally {
      this.countFilesAbortController = null;
    }
  }

  async loadMoreData(params: { offset: number; limit: number }) {
    if (params.offset === 0) {
      return;
    }
    this.lastParams.offset = params.offset;
    this.lastParams.limit = params.limit;

    try {
      const response: API_ViewInformationResponse = await API.newData(this.url(this.currentViewId), this.lastParams);
      runInAction(() => {
        this.files.splice(params.offset, response.data.length, ...response.data);
      });
    } catch (error) {
      throw error;
    }
  }

  getValuableFiltersMap(metadataKey: string) {
    const valuableFiltersMap = {};
    if (!this.stillAvailableMetadata) return valuableFiltersMap;
    for (const metadataValue of getParameterCaseInsensitive(this.stillAvailableMetadata, metadataKey, [])) {
      valuableFiltersMap[metadataValue] = true;
    }
    return valuableFiltersMap;
  }

  getFilesFromIds(fileIds: number[]) {
    const files: API_SearchResult[] = fileIds.reduce((arr, item) => {
      const foundItem = this.files.find((file) => file.id === item);
      if (foundItem) {
        arr.push(foundItem);
      } else {
        console.warn(`FILE ID "${item}" NOT FOUND`);
      }
      return arr;
    }, []);
    return files;
  }

  async downloadFiles(fileIds: number[]) {
    const downloadProgressId = actionProgressStore.init(
      fileIds.length ? i18n.t("Preparing download...", { ns: I18N_NS.PROJECT }) : undefined
    );
    try {
      const files: API_DownloadWizardProperties["files"] = fileIds.map((id) => {
        const fileFound = this.data.find((item) => item.id === id) || ({} as API_ViewInformation);
        if (fileFound.state === "SYMLINK") {
          return { id: fileFound.linkId, state: "SYMLINK" };
        } else {
          return { id: id, state: fileFound.state };
        }
      });
      const cancelToken = actionProgressStore.getCancelToken(downloadProgressId);
      const formBody: API_DownloadWizardProperties = {
        files,
        withDirectories: false,
        withQR: projectsDataStore.isQRForce(),
      };
      const file = await API.getBlobByPost(
        API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId),
        formBody,
        null,
        {
          onDownloadProgress: (evt) => {
            actionProgressStore.setProgress(downloadProgressId, evt);
          },
          cancelToken,
        }
      );

      runInAction(() => {
        download(file, file.name);
      });
    } catch (error) {
      throw error;
    } finally {
      actionProgressStore.dismiss(downloadProgressId);
    }
  }

  setViewId(id: number) {
    this.clearFiles();
    this.viewInformation = {};
    this.currentViewId = id;
  }

  async fetchFilters() {
    try {
      const response = await API.getData<API_ViewInformation>(
        API_URL.VIEWS.getViewInformationObjectForView(projectsDataStore.projectId, this.currentViewId)
      );
      runInAction(() => {
        this.viewInformation = { ...response };
      });
    } catch (error) {
      throw error;
    }
  }

  async selectAll(viewId: number, payload) {
    return await API.newData(API_URL.VIEWS.getAllFiles_1(projectsDataStore.projectId, viewId), payload);
  }

  get currentView() {
    if (!this.currentViewId) {
      return null;
    }
    return this.data.find((item) => item.id === this.currentViewId);
  }

  processResponse() {
    return;
  }
}

export const viewsStore = new ViewsStore();
